import { RWSService } from '@rws-framework/client';
import type {ContentsServiceInstance} from "./ContentsService";
import ContentsService from "./ContentsService";
import type { Course } from "../types/course";
import type { ModeValue } from '../types/mode';

type BreadData = {
  course: string
  data: Array<Breadcrumb>
  index: { [key: string]: number }
}

type Lesson = any
type Page = any

export type Breadcrumb = {
  type: 'lesson' | 'page'
  data: Lesson | Page
  crumbs: (number | null)[]
  url: string
}

class BreadService extends RWSService {
  constructor(
    @ContentsService private contentsService: ContentsServiceInstance
  ) {
    super()
  }

  baked_bread: any = false

  bread(
    course: Course = undefined,
    editModeValue: ModeValue = '',
    user_id: string = '',
    noCache: boolean = false
  ): boolean | BreadData {
    const now = new Date().getTime()

    // This is the clean-the-factory call
    if (!course) {
      this.baked_bread = false
      return false
    }

    // we really need an event model for when course data change! (not
    // changing between courses but course itself changes)
    // return cached data structure if available unless in edit mode
    if (this.baked_bread &&
        (this.baked_bread as BreadData).course === course._id &&
        editModeValue !== "edit" &&
        !noCache
    ) {
      return this.baked_bread as BreadData
    }

    const professorStudentView = this.contentsService.getView(
      course, user_id, editModeValue
    )
    const lessons = (course.lessonsData || []).filter((lesson) => {
      const lessonData = course.course_data[lesson._id] || {}
      const scheduled = new Date(lessonData.open_date).getTime() > now
      return !professorStudentView ||
             !lesson.draft &&
             !(scheduled && lessonData.suppress_until_available)
    })

    const allBreadcrumbs: BreadData = {
      data: [],
      index: {},
      course: course._id
    }

    const lesson_count = lessons.length

    // TODO STARTDATE this again uses pagesData
    lessons.forEach((lesson, i) => {
      const lessonData = course.course_data[lesson._id] || {};
      const lesson_scheduled = new Date(lessonData.open_date).getTime() > now

      let temp: Breadcrumb = {
        type: 'lesson',
        data: lesson,
        crumbs: [i ? -1 : null, null, null, 1],
        url: `/${course._id}/${lesson._id}`
      }

      if (!lesson.pagesData) lesson.pagesData = []

      const pages = lesson.pagesData.filter(page => {
        const pageData = course.course_data[page._id] || {}
        const page_scheduled = new Date(pageData.open_date).getTime() > now
        return !professorStudentView ||
               !page.draft &&
               !(page_scheduled && pageData.suppress_until_available)
      });

      if (!pages && i === lesson_count - 1) {
        // lesson is last and there are no pages so no subsequent crumb
        temp.crumbs[3] = null
      }

      if (!lesson_scheduled || !professorStudentView) {
        // we are still counting future lessons but not displaying them
        // in left nav or breadcrumb
        allBreadcrumbs.data.push(temp)
        allBreadcrumbs.index[temp.url] = allBreadcrumbs.data.length - 1

        let j_counter = 0
        const page_count = pages.filter(page => {
          const e = course.course_data[page._id];
          const scheduled = new Date(e && e.open_date).getTime() > now
          return !scheduled || !professorStudentView
        }).length

        pages.forEach((page, j) => {
          const pageData = course.course_data?.[page._id] || {}
          const page_scheduled = new Date(pageData.open_date).getTime() > now

          if (!page_scheduled || !professorStudentView) {
            // we are still counting future pages but not displaying
            // them in left nav or breadcrumb

            page.lesson_title = lesson.title
            temp = {
              type: "page",
              data: page,
              crumbs: [-1, j_counter, page_count - j_counter, 1],
              url: `/${course._id}/${lesson._id}/${page._id}`
            }

            // we are on first page in first lesson
            if (i === 0 && j === 0) temp.crumbs[0] = -1
            // we are on last page in last lesson
            if (i === lesson_count - 1 && j_counter === page_count - 1) temp.crumbs[3] = null

            allBreadcrumbs.data.push(temp)
            allBreadcrumbs.index[temp.url] = allBreadcrumbs.data.length - 1

            if (!page.draft) j_counter++
          }
        })
      }
    })

    if (professorStudentView) {
      this.baked_bread = allBreadcrumbs
    }

    return allBreadcrumbs
  }
}

export default BreadService.getSingleton()

export {BreadService as BreadServiceInstance}